/**
 * View or download a bill's PDF.
 *
 * The PDF endpoint is auth-gated and streams bytes, so it is fetched as a
 * Blob through the authenticated client and handed to the browser as an
 * object URL. The endpoint URL is never opened directly (a new tab carries
 * no Authorization header).
 *
 * `viewBillPdf` must be called straight from a click handler: the blank tab is
 * opened synchronously, before any await, so iOS Safari and popup blockers
 * allow it. Inside an installed iOS PWA a new tab cannot show a blob URL, so
 * there it falls back to a download.
 */
import { isApiError } from '@finmin/api-client';
import { toast } from '@finmin/ui';
import { openPdfBlob, pdfFilename } from '@finmin/utils';
import { fetchBillPdf, type PartyDocument } from '@/api/billing';

function isIosStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  const isIos =
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  const standalone =
    nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches;
  return isIos && Boolean(standalone);
}

function billFilename(bill: Pick<PartyDocument, 'number' | 'id'>): string {
  return pdfFilename(bill.number ?? `bill-${bill.id.slice(0, 8)}`);
}

async function openBill(bill: Pick<PartyDocument, 'id' | 'number'>, download: boolean): Promise<void> {
  try {
    await openPdfBlob(() => fetchBillPdf(bill.id), billFilename(bill), download);
  } catch (err) {
    toast.error('Could not open the bill', isApiError(err) ? err.message : 'Please try again.');
  }
}

export function viewBillPdf(bill: Pick<PartyDocument, 'id' | 'number'>): Promise<void> {
  return openBill(bill, isIosStandalone());
}

export function downloadBillPdf(bill: Pick<PartyDocument, 'id' | 'number'>): Promise<void> {
  return openBill(bill, true);
}
